import { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import { welcomeSign } from "../../../../appAssets";
import {
  ActiveTool,
  BRUSH_SIZES,
  Path,
  PlacedSticker,
  STICKERS,
  STICKER_SIZE,
  workSheetColors,
  DrawingType,
  pencilBox,
} from "../../config";
import backButtonJson from "../../../../appAssets/jsons/back_button.json";
import undoButtonJson from "../../../../appAssets/jsons/undo_button.json";
import saveButtonJson from "../../../../appAssets/jsons/saveButton.json";
import eraserJson from "../../../../appAssets/jsons/eraser.json";
import pencilJson from "../../../../appAssets/jsons/pencil.json";
import brushJson from "../../../../appAssets/jsons/blackbrush.json";
import "./DrawingCanvas.scss";

interface DrawingCanvasProps {
  onBack: () => void;
  onSave: (dataUrl: string, stickersUsed: number) => void;
  colorSheetUrl?: string;
  drawingType: DrawingType;
  isDisplayPopup: boolean;
  isOnboardingDone?: boolean;
}

interface DragState {
  sticker: { id: string; image: string } | null;
  x: number;
  y: number;
}

function DrawingCanvas({
  onBack,
  onSave,
  colorSheetUrl,
  drawingType,
  isDisplayPopup,
  isOnboardingDone,
}: DrawingCanvasProps) {
  const [paths, setPaths] = useState<Path[]>([]);
  const [currentPath, setCurrentPath] = useState<string>("");
  const [activeTool, setActiveTool] = useState<ActiveTool>("pencil");
  const [brushColor, setBrushColor] = useState<string>(workSheetColors[0]);
  const [brushWidth, setBrushWidth] = useState<number>(
    BRUSH_SIZES[0].brushSize,
  );
  const [stickers, setStickers] = useState<PlacedSticker[]>([]);
  const [drag, setDrag] = useState<DragState>({ sticker: null, x: 0, y: 0 });

  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const trayListRef = useRef<HTMLDivElement | null>(null);
  const [pencilBoxArr, setPencilBox] = useState(pencilBox); // Track if eraser is active
  const [isEraser, setIsEraser] = useState(false);

  const selectedPencil = pencilBoxArr.find((p) => p.isSelected) || pencilBox[0]; // Default to first pencil

  const SCROLL_STEP = 72;
  const scrollTray = (dir: "up" | "down") => {
    trayListRef.current?.scrollBy({
      top: dir === "down" ? SCROLL_STEP : -SCROLL_STEP,
      behavior: "smooth",
    });
  };

  // Convert pointer coords into SVG-local coords
  const toCanvasPoint = (clientX: number, clientY: number) => {
    const rect = canvasWrapRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  // ── Drawing (pointer events handle mouse + touch + pen)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (drag.sticker) return; // ignore if dragging sticker
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const { x, y } = toCanvasPoint(e.clientX, e.clientY);
    setCurrentPath(`M${x},${y}`);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!currentPath) return;
    const { x, y } = toCanvasPoint(e.clientX, e.clientY);
    setCurrentPath((prev) => `${prev} L${x},${y}`);
  };

  const handlePointerUp = () => {
    if (currentPath) {
      setPaths((prev) => [
        ...prev,
        {
          d: currentPath,
          color: brushColor,
          width: brushWidth,
          tool: activeTool,
        },
      ]);
      setCurrentPath("");
    }
  };

  const handleUndo = () => setPaths((prev) => prev.slice(0, -1));

  const handleErase = () => {
    setBrushColor(workSheetColors[9]); // white
    setActiveTool("eraser");
  };

  // ── Save: draw background image directly, then overlay SVG paths/stickers
  const handleSave = async () => {
    const svg = svgRef.current;
    if (!svg) return;
    try {
      const rect = svg.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);

      // Step 1: draw background image directly onto canvas (preserveAspectRatio: xMidYMid meet)
      const bgSrc =
        isOnboardingDone === false
          ? welcomeSign
          : drawingType === "color" && colorSheetUrl
            ? colorSheetUrl
            : null;

      if (bgSrc) {
        await new Promise<void>((resolve) => {
          const bgImg = new Image();
          bgImg.crossOrigin = "anonymous";
          bgImg.onload = () => {
            const scale = Math.min(
              w / bgImg.naturalWidth,
              h / bgImg.naturalHeight,
            );
            const imgW = bgImg.naturalWidth * scale;
            const imgH = bgImg.naturalHeight * scale;
            const imgX = (w - imgW) / 2;
            const imgY = (h - imgH) / 2;
            ctx.drawImage(bgImg, imgX, imgY, imgW, imgH);
            resolve();
          };
          bgImg.onerror = () => resolve();
          bgImg.src = bgSrc;
        });
      }

      // Step 2: serialize SVG without the background image, draw paths/stickers on top
      const svgClone = svg.cloneNode(true) as SVGSVGElement;
      svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      svgClone.setAttribute("width", String(w));
      svgClone.setAttribute("height", String(h));
      if (bgSrc) {
        const firstImage = svgClone.querySelector("image");
        if (firstImage) firstImage.remove();
      }

      const svgString = new XMLSerializer().serializeToString(svgClone);
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = (err) => {
          URL.revokeObjectURL(url);
          reject(err);
        };
        img.src = url;
      });

      onSave(canvas.toDataURL("image/png"), stickers.length);
    } catch {
      onSave("", stickers.length);
    }
  };

  // ── Sticker drag: HTML5 drag image is unreliable, use pointer move tracking
  useEffect(() => {
    if (!drag.sticker) return;
    const onMove = (e: PointerEvent) => {
      setDrag((d) => ({ ...d, x: e.clientX, y: e.clientY }));
    };
    const onUp = (e: PointerEvent) => {
      const wrap = canvasWrapRef.current;
      if (!wrap) {
        setDrag({ sticker: null, x: 0, y: 0 });
        return;
      }
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left - STICKER_SIZE / 2;
      const y = e.clientY - rect.top - STICKER_SIZE / 2;
      const inside =
        x >= 0 &&
        y >= 0 &&
        x <= rect.width - STICKER_SIZE &&
        y <= rect.height - STICKER_SIZE;
      if (inside && drag.sticker) {
        setStickers((prev) => [
          ...prev,
          {
            key: `${drag.sticker!.id}-${Date.now()}-${Math.random()}`,
            id: drag.sticker!.id,
            image: drag.sticker!.image,
            x,
            y,
          },
        ]);
      }
      setDrag({ sticker: null, x: 0, y: 0 });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag.sticker]);

  const startStickerDrag = (
    sticker: { id: string; image: string },
    e: React.PointerEvent,
  ) => {
    setDrag({ sticker, x: e.clientX, y: e.clientY });
  };

  // Long-press to remove a placed sticker (mirrors mobile onLongPress)
  const longPressTimerRef = useRef<number | null>(null);
  const startLongPress = (key: string) => {
    longPressTimerRef.current = window.setTimeout(() => {
      setStickers((prev) => prev.filter((s) => s.key !== key));
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  if (isDisplayPopup) return null;
  // Set pencil type and brush width accordingly
  const handlePencilTypeChange = (box: (typeof pencilBoxArr)[number]) => {
    const localBoxArr = pencilBoxArr.map((item) => {
      return box?.id === item?.id
        ? { ...item, isSelected: true }
        : { ...item, isSelected: false };
    });
    setPencilBox(localBoxArr);
    setBrushWidth(brushWidth);
    // setBrushColor(brushColor);
    setIsEraser(false);
  };

  const handlePencilSelection = (
    selectedBox: (typeof pencilBoxArr)[number],
  ) => {
    setBrushColor(selectedBox.color);
    const updatedPencilBox = pencilBox.map((pencil) =>
      pencil.id === selectedBox.id
        ? { ...pencil, isSelected: true }
        : { ...pencil, isSelected: false },
    );

    setPencilBox(updatedPencilBox);
    setIsEraser(false);
    setBrushWidth(10);
    setActiveTool("pencil");
    handlePencilTypeChange(selectedBox);
  };

  const handlebrushSelection = (selectedBox: (typeof pencilBoxArr)[number]) => {
    const updatedPencilBox = pencilBox.map((pencil) =>
      pencil.id === selectedBox.id
        ? { ...pencil, isSelected: true }
        : { ...pencil, isSelected: false },
    );

    setPencilBox(updatedPencilBox);
    setIsEraser(false);
    setBrushWidth(10);
    setBrushColor(selectedBox.color);
    setActiveTool("brush");
    handlePencilTypeChange(selectedBox);
  };

  // Set color for the brush and reset the eraser state
  const handleSetColor = (color: string) => {
    const selectedBox = pencilBox.find((pencil) => pencil.color === color);

    if (selectedBox) {
      handlePencilSelection(selectedBox);
      handlebrushSelection(selectedBox);
    }
    setBrushColor(color);
    setIsEraser(false);
  };

  return (
    <div className="dc">
      {/* ── Header: back + toolbar + undo/save ── */}
      <div className="dc__header">
        <button className="dc__icon-btn" onClick={onBack} aria-label="Back">
          <Lottie
            animationData={backButtonJson}
            loop={false}
            autoplay={false}
            className="dc__icon-lottie"
          />
        </button>

        <div className="dc__toolbar">
          {/* Brush sizes */}
          <div className="dc__brushes">
            {BRUSH_SIZES.map((size) => (
              <button
                key={size.id}
                type="button"
                className={`dc__brush-dot${brushWidth === size.brushSize ? " dc__brush-dot--active" : ""}`}
                style={{ width: size.width, height: size.width }}
                onClick={() => setBrushWidth(size.brushSize)}
                aria-label={`Brush size ${size.brushSize}`}
              />
            ))}
          </div>

          {/* Pencil / Brush / Eraser */}
          <div className="dc__tools">
            <button
              type="button"
              className={`dc__tool${activeTool === "pencil" ? " dc__tool--active" : ""}`}
              onClick={() => {
                setActiveTool("pencil");
                handlePencilSelection(selectedPencil);
              }}
              aria-label="Pencil"
            >
              <Lottie
                animationData={selectedPencil.iconName}
                loop={false}
                autoplay={false}
                className="dc__tool-lottie"
              />
            </button>
            <button
              type="button"
              className={`dc__tool${activeTool === "brush" ? " dc__tool--active" : ""}`}
              onClick={() => {
                setActiveTool("brush");
                handlebrushSelection(selectedPencil);
              }}
              aria-label="Brush"
            >
              <Lottie
                animationData={selectedPencil?.brushName}
                loop={false}
                autoplay={false}
                className="dc__tool-lottie"
              />
            </button>
            <button
              type="button"
              className={`dc__tool${activeTool === "eraser" ? " dc__tool--active" : ""}`}
              onClick={handleErase}
              aria-label="Eraser"
            >
              <Lottie
                animationData={eraserJson}
                loop={false}
                autoplay={false}
                className="dc__tool-lottie"
              />
            </button>
          </div>

          {/* Color palette */}
          <div className="dc__colors">
            {workSheetColors.map((color) => (
              <button
                key={color}
                type="button"
                className={`dc__color${brushColor === color ? " dc__color--active" : ""}`}
                style={{ background: color }}
                onClick={() => {
                  setBrushColor(color);
                  handleSetColor(color);
                  if (activeTool === "eraser") setActiveTool("pencil");
                }}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>
        </div>

        <div className="dc__header-right">
          <button
            className="dc__icon-btn"
            onClick={handleUndo}
            aria-label="Undo"
          >
            <Lottie
              animationData={undoButtonJson}
              loop={false}
              autoplay={false}
              className="dc__icon-lottie"
            />
          </button>
          {paths.length > 0 && (
            <button
              className="dc__icon-btn"
              onClick={handleSave}
              aria-label="Save"
            >
              <Lottie
                animationData={saveButtonJson}
                loop={false}
                autoplay={false}
                className="dc__icon-lottie"
              />
            </button>
          )}
        </div>
      </div>

      {/* ── Body: sticker tray + canvas ── */}
      <div className="dc__body">
        {/* Sticker tray */}
        {isOnboardingDone && (
          <div className="dc__tray">
            <button
              type="button"
              className="dc__tray-arrow"
              onClick={() => scrollTray("up")}
              aria-label="Scroll stickers up"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>

            <div className="dc__tray-stickers" ref={trayListRef}>
              {STICKERS.map((sticker) => (
                <img
                  key={sticker.id}
                  src={sticker.image}
                  alt={sticker.id}
                  className="dc__tray-sticker"
                  draggable={false}
                  onPointerDown={(e) => startStickerDrag(sticker, e)}
                />
              ))}
            </div>

            <button
              type="button"
              className="dc__tray-arrow"
              onClick={() => scrollTray("down")}
              aria-label="Scroll stickers down"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        )}

        {/* Canvas */}
        <div className="dc__canvas-wrap" ref={canvasWrapRef}>
          <svg
            ref={svgRef}
            className="dc__canvas"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            {/* Optional color sheet / onboarding background */}
            {isOnboardingDone === false ? (
              <image
                href={welcomeSign}
                x={0}
                y={0}
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
              />
            ) : drawingType === "color" && colorSheetUrl ? (
              <image
                href={colorSheetUrl}
                x={0}
                y={0}
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
              />
            ) : null}

            {/* Placed stickers */}
            {stickers.map((s) => (
              <image
                key={s.key}
                href={s.image}
                x={s.x}
                y={s.y}
                width={STICKER_SIZE}
                height={STICKER_SIZE}
                preserveAspectRatio="xMidYMid meet"
                style={{ cursor: "pointer" }}
                onPointerDown={() => startLongPress(s.key)}
                onPointerUp={cancelLongPress}
                onPointerLeave={cancelLongPress}
              />
            ))}

            {/* Committed paths */}
            {paths.map((p, i) => (
              <path
                key={i}
                d={p.d}
                stroke={p.color}
                strokeWidth={p.width}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeOpacity={p.tool === "brush" ? 0.85 : 1}
              />
            ))}

            {/* In-progress path */}
            {currentPath && (
              <path
                d={currentPath}
                stroke={brushColor}
                strokeWidth={brushWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeOpacity={activeTool === "brush" ? 0.85 : 1}
              />
            )}
          </svg>
        </div>
      </div>

      {/* Dragging sticker ghost — follows cursor */}
      {drag.sticker && (
        <img
          src={drag.sticker.image}
          alt=""
          className="dc__drag-ghost"
          style={{
            left: drag.x - STICKER_SIZE / 2,
            top: drag.y - STICKER_SIZE / 2,
          }}
          draggable={false}
        />
      )}
    </div>
  );
}

export default DrawingCanvas;
